import { match } from "assert";
import { ProcessInfo } from "../State/ProcessInfo";
import { mapGetOrCreate } from "./utils/mapGetOrCreate";
import { Search } from "webextension-polyfill";

interface PartiallyMatchedString {
	/**
	 * The string that was (at least partially) matched by descending to this node
	 * in the search state machine.
	 */
	matchString: string;
	/**
	 * The number of characters not yet verified by descending to this node.
	 * 
	 * Used so we need not build out trees beyond the point where there is
	 * a unique possible completion.
	 */
	unmatchedSuffixLength: number;
}
type SearchStateMachineNode = {
	matches?: PartiallyMatchedString | PartiallyMatchedString[];
	charToChildNode?: Map<string, SearchStateMachineNode>;
}

type NodeSuffixPair = [
	SearchStateMachineNode, /*node*/
	SearchStateMachineNode[], /* suffixNodesLongestToShortest */
]



const removeDescendantsOfNodesWithOnlyOneMatch = (node: SearchStateMachineNode): PartiallyMatchedString | undefined => {
	if (node.charToChildNode == null) {
		if (node.matches && !Array.isArray(node.matches)) {
			return node.matches;
		} else {
			return;
		}
	}
	const children = [...node.charToChildNode.values()];
	const childResults = children.map( removeDescendantsOfNodesWithOnlyOneMatch );
	const [onlyChildResult] = childResults.length === 1 ? childResults : [];
	if (onlyChildResult != null &&  node.matches == null) {
		delete node.charToChildNode;
		node.matches = {matchString: onlyChildResult.matchString, unmatchedSuffixLength: onlyChildResult.unmatchedSuffixLength + 1};
		return node.matches;
	}
}

/**
 * @param stringsToSearchFor 
 * @returns 
 */
const buildSearchStateMachine = (stringsToSearchFor: string[], caseSensitive: boolean) => {
	ProcessInfo.log(`Building search state machine after ${ProcessInfo.updateTime()}ms`);
	let nodesCreated = 0;
	const createNode = (): SearchStateMachineNode => {
		nodesCreated++;
		return {};
	}
	// Start with the root
	const root = createNode();

	// Then build out nodes for every character of every search term
	for (const searchTerm of stringsToSearchFor) {
		if (searchTerm.length === 0) continue;
		// Stepping through the search term to create a node for each prefix
		let currentNode = root;
		for (const mixedCaseChar of searchTerm) {
			const char = caseSensitive ? mixedCaseChar : mixedCaseChar.toLocaleLowerCase();
			currentNode = mapGetOrCreate(currentNode.charToChildNode ??= new Map(), char, createNode);
		}
		currentNode.matches = {matchString: searchTerm, unmatchedSuffixLength: 0};
	}

//	if (nodesCreated > 256) {
		removeDescendantsOfNodesWithOnlyOneMatch(root);
//	}

	ProcessInfo.log(`Calculating next nodes start at ${ProcessInfo.updateTime()}ms`);
	let nodeSuffixPairsAtThisDepth: NodeSuffixPair[] = [[root, []]];
	while (nodeSuffixPairsAtThisDepth.length > 0) {
		// Create a new node at the next depth for each child, walking
		// down its suffix set.
		const nodeSuffixPairsAtNextLevelOfDepth = ([] as NodeSuffixPair[]).concat(
			...nodeSuffixPairsAtThisDepth.map( ([node, suffixNodesLongestToShortest]) =>
				[...(node.charToChildNode?.entries() ?? [])]
					.map( ([char, child]) => {
						// The suffixes of a child can be found by taking the suffixes of the parent,
						// and following each node down the tree to its next character.
						// Lastly, the root node must be added as it is a suffix of every string.
						const childSuffixNodesLongestToShortest =
							[...suffixNodesLongestToShortest]
							.map( suffixNode => suffixNode.charToChildNode?.get(char))
							.filter( suffix => suffix != null );
						// 
						childSuffixNodesLongestToShortest.push(root);
						return [child, childSuffixNodesLongestToShortest] as NodeSuffixPair;
					})
			)
		);
		for (const [node, suffixesLongestToShortest] of nodeSuffixPairsAtThisDepth) {
			node.charToChildNode ??= new Map();
			// Since the longest suffix node is still a suffix of this string, and thus shorter than it,
			// we've already ensured it is fully connected down the tree.
			// So, we can ensure this node is full connected by ensuring that, for any successor character
			// that is known to the longest suffix but not to this node, we connect this node to the
			// successor for the suffix.
			// Example:
			//   If the node abcd has longest known suffix bc, and the node bc maps f to node bcf,
			//   then the node for abcd should map f to the node bcf.
			const [longestSuffixNode] = suffixesLongestToShortest;
			for (const [suffixChar, suffixChild] of longestSuffixNode?.charToChildNode?.entries() ?? []) {
				if (!node.charToChildNode.has(suffixChar)) {
					node.charToChildNode.set(suffixChar, suffixChild);
				}
			}
			// If the suffix matches a string, this longer string should also match it
			for (const suffixNode of suffixesLongestToShortest) {
				if (suffixNode.matches != null) {
					const nodeMatches = node.matches == null ? [] : Array.isArray(node.matches) ? node.matches : [node.matches];
					const nodeMatchesSet = new Set(nodeMatches.map( m => m.matchString));
					const suffixMatches = Array.isArray(suffixNode.matches) ? suffixNode.matches : [suffixNode.matches];
					const filteredSuffixMatches = suffixMatches.filter( m => !nodeMatchesSet.has(m.matchString)); 					
					const matches = [...nodeMatches, ...filteredSuffixMatches];
					node.matches = matches.length === 1 ? matches[0] : matches;
				}
			}
		}
		nodeSuffixPairsAtThisDepth = nodeSuffixPairsAtNextLevelOfDepth;
	}
	ProcessInfo.log(`State machine created ${nodesCreated} nodes in ${ProcessInfo.updateTime()}ms`);

	return root;
}

export interface EfficientStringSearchResult {
	position: number;
	stringFound: string;
	matchedString: string;
	matchedStringBeforeForcedToLowerCase?: string;
}


export type EfficientStringSearchFunction = (text: string) => EfficientStringSearchResult[];

export const getEfficientFunctionToSearchMultipleStrings = (caseSensitivity: "case-sensitive" | "case-insensitive") => (stringsToSearchFor: Iterable<string>) => {
	const caseSensitive = caseSensitivity === "case-sensitive";
	ProcessInfo.log(`Entered getEfficientFunctionToSearchMultipleStrings after ${ProcessInfo.updateTime()}ms`);
	const stateMachineRootNode = (buildSearchStateMachine([...stringsToSearchFor], caseSensitive));
	ProcessInfo.log(`Built state machine after ${ProcessInfo.updateTime()}ms`);

	const search: EfficientStringSearchFunction = (
		text: string
	): EfficientStringSearchResult[] => {
		let results: EfficientStringSearchResult[] = [];
		let searchPosition = 0;
		let stateMachineNode = stateMachineRootNode;
		// Move through state machine while walking through text to search
		for (const charMixedCase of text) {
			const char = caseSensitive ? charMixedCase : charMixedCase.toLocaleLowerCase();
			searchPosition++;
			stateMachineNode = stateMachineNode.charToChildNode?.get(char) ?? stateMachineRootNode.charToChildNode?.get(char) ?? stateMachineRootNode;
			if (stateMachineNode.matches != null) {
				const candidateMatches = Array.isArray(stateMachineNode.matches) ? stateMachineNode.matches : [stateMachineNode.matches];
				const fullMatches = candidateMatches.filter( match => {
					const numCharsAlreadyMatched = match.matchString.length - match.unmatchedSuffixLength;					
					for (let i = 0; i < match.unmatchedSuffixLength; i++) {
						if (caseSensitive && text[searchPosition + i] !== match.matchString[numCharsAlreadyMatched + i]) {
							return false;
						} else if (!caseSensitive && text[searchPosition + i]?.toLocaleLowerCase() !== match.matchString[numCharsAlreadyMatched + i]?.toLocaleLowerCase()) {
							return false;
						}
					}
					// no un-matching characters found
					return true;
				});
				const matches = fullMatches.map( match => {
					const position = searchPosition - match.matchString.length + match.unmatchedSuffixLength;
					const stringFound = text.slice(position, position + match.matchString.length);
					return {
						position,
						matchedString: match.matchString,
						stringFound,
						caseSensitiveMatch: stringFound === match.matchString
					}
				});
				const caseSensitiveMatches = matches.filter( ({caseSensitiveMatch}) => caseSensitiveMatch);
				const caseSensitiveMatchesLc = new Set<string>(caseSensitiveMatches.map( ({matchedString}) => matchedString.toLocaleLowerCase()));
				// Remove case-insensitive matches that are already found in case-sensitive matches
				const caseInsensitiveMatches = matches.filter( m => !m.caseSensitiveMatch && !caseSensitiveMatchesLc.has(m.stringFound.toLocaleLowerCase()));
				const nonDuplicatedMatches = [...caseSensitiveMatches, ...caseInsensitiveMatches]; 
				results.push(...nonDuplicatedMatches);
			}
		}
		return results;
	}
	return search;
}


export const getEfficientFunctionToSearchMultipleStringsCaseSensitive = getEfficientFunctionToSearchMultipleStrings("case-sensitive");
export const getEfficientFunctionToSearchMultipleStringsCaseInsensitive = getEfficientFunctionToSearchMultipleStrings("case-insensitive");